// Manages in-memory photo state and IndexedDB persistence for the inspection report form.
//
// Two section types:
//   - System sections (hvac, plumbing): photos keyed by system UUID in a Map.
//     The UUID comes from HvacSystemValues.id / PlumbingSystemValues.id, which is
//     stable across re-renders and draft restores. This is critical — using array
//     index would misalign photos if systems are reordered or removed.
//   - Flat sections (electrical, waterTreatment, airQuality): simple File[] state,
//     stored with FLAT_SENTINEL ('_') as the systemId in IndexedDB.
//
// Restoration lifecycle (controlled by draftCheckComplete + hasDraft):
//   - draftCheckComplete=false → effect is skipped entirely (draft check still in progress)
//   - draftCheckComplete=true, hasDraft=false → deleteMediaForJob (orphan cleanup)
//   - draftCheckComplete=true, hasDraft=true  → restore all slots from IndexedDB
//
// The draftCheckComplete gate is necessary because useInspectionReportDraft and
// useMediaDraft both run their mount effects in the same React flush. Without it,
// useMediaDraft would see hasDraft=false (the initial value) and delete blobs before
// useInspectionReportDraft has had a chance to set hasDraft=true from a stored draft.
//
// All IndexedDB operations are fire-and-forget with .catch(() => {}) — the form
// works normally even if IndexedDB is unavailable (private browsing, storage blocked).

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { saveMediaSlot, loadMediaSlot, deleteMediaForJob } from '../utils/mediaDraftStorage';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import type { HvacSystemValues } from '../components/HvacSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';

type FlatSection = 'electrical' | 'waterTreatment' | 'airQuality';
type SystemSection = 'hvac' | 'plumbing';

const FLAT_SECTIONS: FlatSection[] = ['electrical', 'waterTreatment', 'airQuality'];
// Flat sections have no per-system identity, so we use a sentinel as the systemId slot key.
const FLAT_SENTINEL = '_';

const QUOTA_WARNING =
  'This file could not be saved to your draft due to storage limits. It will be lost if you leave this page.';

interface UseMediaDraftParams {
  homeInspectionJobId: string | undefined;
  hvacSystems: HvacSystemValues[];
  plumbingSystems: PlumbingSystemValues[];
  /** True once useInspectionReportDraft has finished its localStorage check. */
  hasDraft: boolean;
  /** Gates the cleanup/restore effect — prevents acting on the initial hasDraft=false. */
  draftCheckComplete: boolean;
  /** Fired when files are ADDED to a slot (not on removal or draft restore). */
  onMediaAdded?: (kind: 'photo' | 'video', count: number) => void;
}

interface UseMediaDraftResult {
  hvacSystemPhotos: File[][];
  plumbingSystemPhotos: File[][];
  electricalPhotos: File[];
  waterTreatmentPhotos: File[];
  airQualityPhotos: File[];
  mediaCount: number;
  updateSystemMedia: (type: SystemSection, systemId: string, files: File[]) => Promise<void>;
  updateFlatMedia: (section: FlatSection, files: File[]) => Promise<void>;
  removeSystemMedia: (type: SystemSection, systemId: string) => Promise<void>;
  clearAllMedia: (jobId: string) => Promise<void>;
}

// Covers Chrome ('QuotaExceededError'), Firefox ('NS_ERROR_DOM_QUOTA_REACHED'),
// and Safari legacy (DOMException.code === 22).
function isQuotaError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED' || (err as DOMException).code === 22
  );
}

// Compare against the previous slot contents so only genuine ADDS are reported.
// Removals and draft restores must not inflate the counts.
//
// Contract: callers must pass append-only arrays (MediaUploadSection always does
// `[...existing, ...newFiles]`). Length growth + slice(previous.length) is how
// we detect adds — a full non-append replace would mis-count.
function reportAddedMedia(
  previous: File[],
  next: File[],
  onMediaAdded?: (kind: 'photo' | 'video', count: number) => void,
): void {
  if (!onMediaAdded || next.length <= previous.length) return;

  const added = next.slice(previous.length);
  const videos = added.filter((file) => file.type.startsWith('video/')).length;
  const photos = added.length - videos;

  if (photos > 0) onMediaAdded('photo', photos);
  if (videos > 0) onMediaAdded('video', videos);
}

export function useMediaDraft({
  homeInspectionJobId,
  hvacSystems,
  plumbingSystems,
  hasDraft,
  draftCheckComplete,
  onMediaAdded,
}: UseMediaDraftParams): UseMediaDraftResult {
  const { setSnackBarMessage, handleSnackbarAlertSeverityChange, setSnackBarOpen } = useContext(SnackBarContext);

  // Maps keyed by system UUID — order-independent, safe when systems are added/removed.
  const [hvacMediaMap, setHvacMediaMap] = useState<Map<string, File[]>>(new Map());
  const [plumbingMediaMap, setPlumbingMediaMap] = useState<Map<string, File[]>>(new Map());
  const [electricalPhotos, setElectricalPhotos] = useState<File[]>([]);
  const [waterTreatmentPhotos, setWaterTreatmentPhotos] = useState<File[]>([]);
  const [airQualityPhotos, setAirQualityPhotos] = useState<File[]>([]);

  // Refs so async restore callbacks always see the current system list,
  // not the stale snapshot captured at the time the effect ran.
  const hvacSystemsRef = useRef(hvacSystems);
  const plumbingSystemsRef = useRef(plumbingSystems);
  useEffect(() => {
    hvacSystemsRef.current = hvacSystems;
  }, [hvacSystems]);
  useEffect(() => {
    plumbingSystemsRef.current = plumbingSystems;
  }, [plumbingSystems]);

  const showQuotaWarning = useCallback(() => {
    setSnackBarMessage?.(QUOTA_WARNING);
    handleSnackbarAlertSeverityChange?.('warning');
    setSnackBarOpen?.(true);
  }, [setSnackBarMessage, handleSnackbarAlertSeverityChange, setSnackBarOpen]);

  // Cleanup or restore on mount. Waits for draftCheckComplete so hasDraft reflects
  // the real localStorage state, not the React initial value of false.
  useEffect(() => {
    if (!homeInspectionJobId || !draftCheckComplete) return;

    if (!hasDraft) {
      // No text draft exists — delete any blobs left from a previous abandoned session.
      deleteMediaForJob(homeInspectionJobId).catch(() => {});
      return;
    }

    // Text draft found — restore all slots from IndexedDB in parallel.
    const currentHvac = hvacSystemsRef.current;
    const currentPlumbing = plumbingSystemsRef.current;

    const hvacLoads = currentHvac.map((sys) =>
      loadMediaSlot(homeInspectionJobId, 'hvac', sys.id).then((files) => ({ id: sys.id, files })),
    );
    const plumbingLoads = currentPlumbing.map((sys) =>
      loadMediaSlot(homeInspectionJobId, 'plumbing', sys.id).then((files) => ({ id: sys.id, files })),
    );
    const flatLoads = FLAT_SECTIONS.map((section) =>
      loadMediaSlot(homeInspectionJobId, section, FLAT_SENTINEL).then((files) => ({ section, files })),
    );

    // .catch(() => {}) degrades gracefully if IndexedDB is unavailable (e.g. private browsing)
    Promise.all(hvacLoads)
      .then((results) => {
        setHvacMediaMap(new Map(results.map(({ id, files }) => [id, files])));
      })
      .catch(() => {});

    Promise.all(plumbingLoads)
      .then((results) => {
        setPlumbingMediaMap(new Map(results.map(({ id, files }) => [id, files])));
      })
      .catch(() => {});

    Promise.all(flatLoads)
      .then((results) => {
        results.forEach(({ section, files }) => {
          if (section === 'electrical') setElectricalPhotos(files);
          if (section === 'waterTreatment') setWaterTreatmentPhotos(files);
          if (section === 'airQuality') setAirQualityPhotos(files);
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeInspectionJobId, hasDraft, draftCheckComplete]);

  const updateSystemMedia = useCallback(
    async (type: SystemSection, systemId: string, files: File[]) => {
      const map = type === 'hvac' ? hvacMediaMap : plumbingMediaMap;
      reportAddedMedia(map.get(systemId) ?? [], files, onMediaAdded);

      // Update state immediately for responsive UI, then persist async.
      if (type === 'hvac') {
        setHvacMediaMap((prev) => new Map(prev).set(systemId, files));
      } else {
        setPlumbingMediaMap((prev) => new Map(prev).set(systemId, files));
      }

      if (!homeInspectionJobId) return;

      try {
        await saveMediaSlot(homeInspectionJobId, type, systemId, files);
      } catch (err) {
        if (isQuotaError(err)) {
          showQuotaWarning();
        }
        // Other errors are silently ignored (progressive enhancement)
      }
    },
    [homeInspectionJobId, showQuotaWarning, hvacMediaMap, plumbingMediaMap, onMediaAdded],
  );

  const updateFlatMedia = useCallback(
    async (section: FlatSection, files: File[]) => {
      // if/else rather than a nested ternary — airbnb-typescript bans no-nested-ternary.
      let current: File[] = airQualityPhotos;
      if (section === 'electrical') current = electricalPhotos;
      else if (section === 'waterTreatment') current = waterTreatmentPhotos;
      reportAddedMedia(current, files, onMediaAdded);

      if (section === 'electrical') setElectricalPhotos(files);
      if (section === 'waterTreatment') setWaterTreatmentPhotos(files);
      if (section === 'airQuality') setAirQualityPhotos(files);

      if (!homeInspectionJobId) return;

      try {
        await saveMediaSlot(homeInspectionJobId, section, FLAT_SENTINEL, files);
      } catch (err) {
        if (isQuotaError(err)) {
          showQuotaWarning();
        }
        // Other errors are silently ignored (progressive enhancement)
      }
    },
    [homeInspectionJobId, showQuotaWarning, electricalPhotos, waterTreatmentPhotos, airQualityPhotos, onMediaAdded],
  );

  const removeSystemMedia = useCallback(
    async (type: SystemSection, systemId: string) => {
      if (type === 'hvac') {
        setHvacMediaMap((prev) => {
          const next = new Map(prev);
          next.delete(systemId);
          return next;
        });
      } else {
        setPlumbingMediaMap((prev) => {
          const next = new Map(prev);
          next.delete(systemId);
          return next;
        });
      }

      if (!homeInspectionJobId) return;

      // Overwrite with an empty array rather than a targeted delete — reuses the
      // slot-replace logic in saveMediaSlot and keeps the code path uniform.
      await saveMediaSlot(homeInspectionJobId, type, systemId, []).catch(() => {});
    },
    [homeInspectionJobId],
  );

  // jobId is passed explicitly (not closed over homeInspectionJobId) so the caller
  // cannot accidentally use a stale closure value at submit time.
  const clearAllMedia = useCallback(async (jobId: string) => {
    setHvacMediaMap(new Map());
    setPlumbingMediaMap(new Map());
    setElectricalPhotos([]);
    setWaterTreatmentPhotos([]);
    setAirQualityPhotos([]);
    await deleteMediaForJob(jobId).catch(() => {});
  }, []);

  // Convert UUID-keyed maps to ordered arrays matching the system list order.
  // Components receive File[][] indexed by position, not by UUID.
  const hvacSystemPhotos = useMemo(
    () => hvacSystems.map((sys) => hvacMediaMap.get(sys.id) ?? []),
    [hvacSystems, hvacMediaMap],
  );

  const plumbingSystemPhotos = useMemo(
    () => plumbingSystems.map((sys) => plumbingMediaMap.get(sys.id) ?? []),
    [plumbingSystems, plumbingMediaMap],
  );

  const mediaCount = useMemo(() => {
    const hvacTotal = [...hvacMediaMap.values()].reduce((sum, files) => sum + files.length, 0);
    const plumbingTotal = [...plumbingMediaMap.values()].reduce((sum, files) => sum + files.length, 0);
    return hvacTotal + plumbingTotal + electricalPhotos.length + waterTreatmentPhotos.length + airQualityPhotos.length;
  }, [hvacMediaMap, plumbingMediaMap, electricalPhotos, waterTreatmentPhotos, airQualityPhotos]);

  return {
    hvacSystemPhotos,
    plumbingSystemPhotos,
    electricalPhotos,
    waterTreatmentPhotos,
    airQualityPhotos,
    mediaCount,
    updateSystemMedia,
    updateFlatMedia,
    removeSystemMedia,
    clearAllMedia,
  };
}
